import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings, Server, Bell, MapPin, Vibrate, Moon, Sun, Github
} from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';

function Toggle({ value, onChange }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative w-12 h-7 rounded-full transition ${
        value ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-700'
      }`}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        className={`absolute top-0.5 ${value ? 'left-[22px]' : 'left-0.5'} w-6 h-6 rounded-full bg-white shadow`}
      />
    </button>
  );
}

function Row({ icon: Icon, title, desc, action }) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-2xl bg-black/[0.03] dark:bg-white/[0.04]">
      <span className="w-10 h-10 grid place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm">{title}</p>
        <p className="text-xs opacity-70">{desc}</p>
      </div>
      <div>{action}</div>
    </div>
  );
}

export default function SettingsPage() {
  const { connected, status, TOPICS, geoStatus } = useMQTT();
  const [notif, setNotif] = useState(true);
  const [haptics, setHaptics] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.classList.toggle('dark', next === 'dark');
    document.body.classList.toggle('dark-theme', next === 'dark');
  };

  return (
    <div className="grid gap-4 sm:gap-5">
      <motion.div layout className="card-surface p-5 flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-slate-500 to-slate-700 grid place-items-center text-white shadow-glow">
          <Settings className="w-5 h-5" />
        </span>
        <div>
          <h2 className="text-lg font-extrabold tracking-tight">Settings</h2>
          <p className="text-sm opacity-70">Preferences and device configuration</p>
        </div>
      </motion.div>

      <div className="card-surface p-5 grid gap-3">
        <p className="text-xs font-bold uppercase tracking-widest opacity-60">Connection</p>
        <Row
          icon={Server}
          title={`MQTT — ${connected ? 'Connected' : 'Offline'}`}
          desc={`${status} · topic: ${TOPICS?.sensors || 'container/sensors'}`}
          action={
            <span className={`chip ${
              connected
                ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                : 'bg-red-500/15 text-red-600 dark:text-red-400'
            }`}>
              {connected ? 'Online' : 'Offline'}
            </span>
          }
        />
        <Row
          icon={MapPin}
          title="GPS Tracking"
          desc={`Status: ${geoStatus}`}
          action={
            <span className="chip bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
              {geoStatus === 'live' ? 'Live' : 'Idle'}
            </span>
          }
        />
      </div>

      <div className="card-surface p-5 grid gap-3">
        <p className="text-xs font-bold uppercase tracking-widest opacity-60">Preferences</p>
        <Row
          icon={theme === 'dark' ? Moon : Sun}
          title="Theme"
          desc={`Current: ${theme}`}
          action={<Toggle value={theme === 'dark'} onChange={() => toggleTheme()} />}
        />
        <Row
          icon={Bell}
          title="Push notifications"
          desc="Alert on critical events"
          action={<Toggle value={notif} onChange={setNotif} />}
        />
        <Row
          icon={Vibrate}
          title="Haptic feedback"
          desc="Subtle vibration on interactions"
          action={<Toggle value={haptics} onChange={setHaptics} />}
        />
      </div>

      <div className="card-surface p-5 grid gap-2">
        <p className="text-xs font-bold uppercase tracking-widest opacity-60">About</p>
        <p className="text-sm">
          <span className="font-bold">Container Monitor AI</span> · v1.0
        </p>
        <p className="text-xs opacity-70">
          Live ESP8266 / STM32 telemetry · realtime GPS tracking · on-device AI scoring.
        </p>
        <a
          href="https://github.com/"
          target="_blank" rel="noreferrer"
          className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-indigo-600 dark:text-indigo-400"
        >
          <Github className="w-4 h-4" /> Source
        </a>
      </div>
    </div>
  );
}
